/**
 * AuthPage - Authentication page using Ant Design components
 */

import React, { useState } from "react";

import {
  CloseOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  StarOutlined,
} from "@ant-design/icons";
import {
  Layout,
  Card,
  Typography,
  Space,
  Tabs,
  Button,
  Row,
  Col,
  Result,
  Avatar,
  Tag,
} from "antd";
import { useNavigate } from "react-router-dom";

import { Logo } from "@falcon-terminal/shared-ui/components/common";

import { TERMINAL_ROUTES } from "@/features/terminal/routes";

import { useLogin } from "../api/authApi";
import { LoginForm } from "../components/LoginForm";
import { RequestAccessForm } from "../components/RequestAccessForm";

import type { LoginFormData } from "../validations/loginSchema";

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

type AuthTab = "password" | "request-access";

export const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AuthTab>("request-access");
  const [requestSuccess, setRequestSuccess] = useState(false);

  const {
    mutateAsync: login,
    isPending: isLoginPending,
    error: loginError,
  } = useLogin();

  const handleLogin = async (data: LoginFormData) => {
    try {
      await login({ email: data.email, password: data.password });
      navigate(TERMINAL_ROUTES.HOME);
    } catch {
      // Error handled by React Query
    }
  };

  const handleRequestAccess = async () => {
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setRequestSuccess(true);
  };

  const tabItems = [
    {
      key: "password",
      label: "Sign In",
      children: (
        <LoginForm
          onSubmit={handleLogin}
          isLoading={isLoginPending}
          error={loginError?.message}
        />
      ),
    },
    {
      key: "request-access",
      label: "Request Access",
      children: requestSuccess ? (
        <Result
          icon={<RocketOutlined style={{ color: "#BB8800" }} />}
          title="You're on the list!"
          subTitle="We'll review your request and send you an invite within 24-48 hours."
          extra={[
            <Button
              key="signin"
              type="link"
              onClick={() => setActiveTab("password")}
              style={{ color: "#BB8800" }}
            >
              Already have access? Sign in →
            </Button>,
          ]}
        />
      ) : (
        <RequestAccessForm onSubmit={handleRequestAccess} />
      ),
    },
  ];

  return (
    <Layout
      style={{ height: "100vh", background: "#0a0a0a", overflow: "auto" }}
    >
      <Content style={{ padding: 0 }}>
        <Row
          gutter={[48, 48]}
          align="middle"
          style={{
            minHeight: "100vh",
            padding: "24px 24px 48px",
            maxWidth: "1440px",
            margin: "0 auto",
          }}
        >
          {/* Left Side - Marketing Content */}
          <Col xs={24} lg={13} xl={14}>
            <Space
              direction="vertical"
              size={32}
              style={{ width: "100%", padding: "0 16px" }}
            >
              {/* Logo */}
              <Space direction="vertical" size={8}>
                <Logo variant="horizontal" height={48} />
                <Tag
                  style={{
                    fontFamily: "monospace",
                    fontSize: "10px",
                    letterSpacing: "0.25em",
                    border: "1px solid rgba(187, 136, 0, 0.25)",
                    background: "rgba(187, 136, 0, 0.08)",
                    color: "#BB8800",
                    padding: "4px 12px",
                    textTransform: "uppercase",
                  }}
                >
                  Idea → Insights → Decisions
                </Tag>
              </Space>

              {/* Main Heading */}
              <Title
                level={1}
                style={{
                  color: "#ffffff",
                  fontSize: "clamp(2rem, 6vw, 4rem)",
                  lineHeight: 1.15,
                  margin: 0,
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                }}
              >
                De-risk product bets in{" "}
                <span style={{ color: "#BB8800", fontWeight: 700 }}>hours</span>
                ,
                <br />
                not weeks.
              </Title>

              {/* Description */}
              <Paragraph
                style={{
                  color: "rgba(224, 224, 224, 0.7)",
                  fontSize: "clamp(15px, 1.8vw, 18px)",
                  maxWidth: "560px",
                  lineHeight: 1.7,
                  marginBottom: 0,
                }}
              >
                The first-mile AI-native B2B SaaS platform for product teams to
                validate ideas and co-create with real users, at scale.
              </Paragraph>

              {/* Trust Indicators */}
              <Space size={16} wrap style={{ marginTop: "16px" }}>
                <Space size={8}>
                  <Avatar
                    icon={<ThunderboltOutlined />}
                    style={{
                      backgroundColor: "rgba(187, 136, 0, 0.12)",
                      border: "1px solid rgba(187, 136, 0, 0.25)",
                      color: "#BB8800",
                      width: "32px",
                      height: "32px",
                    }}
                  />
                  <Text
                    style={{
                      color: "rgba(224, 224, 224, 0.75)",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    AI-Powered
                  </Text>
                </Space>
                <Space size={8}>
                  <Avatar
                    icon={<SafetyOutlined />}
                    style={{
                      backgroundColor: "rgba(187, 136, 0, 0.12)",
                      border: "1px solid rgba(187, 136, 0, 0.25)",
                      color: "#BB8800",
                      width: "32px",
                      height: "32px",
                    }}
                  />
                  <Text
                    style={{
                      color: "rgba(224, 224, 224, 0.75)",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    Enterprise Ready
                  </Text>
                </Space>
                <Space size={8}>
                  <Avatar
                    icon={<StarOutlined />}
                    style={{
                      backgroundColor: "rgba(187, 136, 0, 0.12)",
                      border: "1px solid rgba(187, 136, 0, 0.25)",
                      color: "#BB8800",
                      width: "32px",
                      height: "32px",
                    }}
                  />
                  <Text
                    style={{
                      color: "rgba(224, 224, 224, 0.75)",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    10x Faster
                  </Text>
                </Space>
              </Space>
            </Space>
          </Col>

          {/* Right Side - Auth Card */}
          <Col xs={24} lg={11} xl={10}>
            <Row justify="center" align="middle">
              <Col
                xs={24}
                sm={22}
                md={20}
                lg={24}
                style={{ width: "100%", maxWidth: "500px" }}
              >
                <Card
                  style={{
                    width: "100%",
                    borderRadius: "12px",
                    background: "rgba(26, 26, 26, 0.98)",
                    border: "1px solid rgba(187, 136, 0, 0.15)",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
                  }}
                  title={
                    <Row justify="space-between" align="top">
                      <Col>
                        <Space direction="vertical" size={4}>
                          <Title
                            level={4}
                            style={{
                              margin: 0,
                              color: "#fff",
                              fontSize: "22px",
                              fontWeight: 600,
                            }}
                          >
                            Access The Platform
                          </Title>
                          <Text type="secondary" style={{ fontSize: "14px" }}>
                            Join thousands of product teams
                          </Text>
                        </Space>
                      </Col>
                      <Col>
                        <Button
                          type="text"
                          icon={<CloseOutlined />}
                          onClick={() => navigate("/")}
                          style={{ color: "rgba(224, 224, 224, 0.5)" }}
                        />
                      </Col>
                    </Row>
                  }
                  styles={{
                    header: {
                      borderBottom: "1px solid rgba(187, 136, 0, 0.15)",
                      padding: "28px 28px 20px",
                    },
                    body: { padding: "28px" },
                  }}
                >
                  <Tabs
                    activeKey={activeTab}
                    onChange={(key) => {
                      setActiveTab(key as AuthTab);
                      if (key === "request-access") {
                        setRequestSuccess(false);
                      }
                    }}
                    items={tabItems}
                    style={{ marginBottom: 0 }}
                    size="large"
                  />

                  <Row justify="center" style={{ marginTop: "24px" }}>
                    <Col>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        By continuing, you agree to our{" "}
                        <Button
                          type="link"
                          style={{
                            color: "#BB8800",
                            padding: 0,
                            height: "auto",
                            fontSize: "inherit",
                          }}
                        >
                          Terms
                        </Button>{" "}
                        and{" "}
                        <Button
                          type="link"
                          style={{
                            color: "#BB8800",
                            padding: 0,
                            height: "auto",
                            fontSize: "inherit",
                          }}
                        >
                          Privacy Policy
                        </Button>
                      </Text>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default AuthPage;
